import { BIG_ONE, BIG_ZERO } from "@/lib/tool-kit/math/bigint";

/**
 * Pascal's triangle on BigInt: row n holds C(n, 0) … C(n, n), each entry the sum of the two
 * above it. Row 99 already has entries of 29 digits, past what a double stores.
 */

export const MAX_ROWS = 100;
export const DEFAULT_ROWS = 12;

export type Highlight = "none" | "odd" | "multiples" | "diagonal" | "shallow";

/** Rows 0 … count − 1. */
export function pascalRows(count: number): bigint[][] {
  const rows: bigint[][] = [];
  for (let n = 0; n < count; n += 1) {
    const previous = rows[n - 1];
    const row: bigint[] = [];
    for (let k = 0; k <= n; k += 1) {
      row.push(k === 0 || k === n ? BIG_ONE : previous[k - 1] + previous[k]);
    }
    rows.push(row);
  }
  return rows;
}

export interface HighlightRule {
  mode: Highlight;
  /** m for "multiples", d for "diagonal", s for "shallow". */
  parameter: number;
}

/** Is entry C(row, col) picked out by the rule? */
export function isHighlighted(
  value: bigint,
  row: number,
  col: number,
  rule: HighlightRule
): boolean {
  switch (rule.mode) {
    case "odd":
      return value % BigInt(2) === BIG_ONE;
    case "multiples":
      return rule.parameter >= 2 && value % BigInt(rule.parameter) === BIG_ZERO;
    case "diagonal":
      return col === rule.parameter || row - col === rule.parameter;
    case "shallow":
      return row + col === rule.parameter;
    default:
      return false;
  }
}

export interface HighlightStats {
  highlighted: number;
  total: number;
  /** The sum of the highlighted entries. */
  sum: bigint;
}

export function highlightStats(
  rows: readonly bigint[][],
  rule: HighlightRule
): HighlightStats {
  let highlighted = 0;
  let total = 0;
  let sum = BIG_ZERO;
  for (const [n, row] of rows.entries()) {
    for (const [k, value] of row.entries()) {
      total += 1;
      if (isHighlighted(value, n, k, rule)) {
        highlighted += 1;
        sum += value;
      }
    }
  }
  return { highlighted, total, sum };
}

/** The longest entry, in digits. */
export function widestEntry(rows: readonly bigint[][]): number {
  const last = rows.at(-1) ?? [];
  return last.reduce(
    (widest, value) => Math.max(widest, value.toString().length),
    1
  );
}

/** The triangle as text, one row per line — centred with spaces, or left-aligned. */
export function triangleText(
  rows: readonly bigint[][],
  centred: boolean
): string {
  const width = widestEntry(rows);
  const lines = rows.map((row) =>
    row.map((value) => value.toString().padStart(centred ? width : 0)).join(" ")
  );
  if (!centred) {
    return lines.join("\n");
  }
  const longest = Math.max(...lines.map((line) => line.length));
  return lines
    .map(
      (line) => `${" ".repeat(Math.floor((longest - line.length) / 2))}${line}`
    )
    .join("\n");
}

export function rowSum(row: readonly bigint[]): bigint {
  return row.reduce((total, value) => total + value, BIG_ZERO);
}
